import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'

import Drawer from './drawer'
import { getUnreadCount } from '../libs/store/lists/unread'
import { isNull } from 'lodash'
import { useClasses } from 'lib/utilities/react'
import { useWatchableRef } from 'lib/hooks'
import { distanceFromLeft, distanceFromTop } from 'lib/utilities/dom'
import { displayMax } from '../utils'
import { useUnreadContext } from './contexts/unread'
import { useMessages } from '../libs/store/common/messages'
import { useMessageThreads } from '../libs/store/common/threads'
import { useOwners } from '../libs/store/common/owners'
import { useOwnerUsers } from '../libs/store/common/owner_users'


const MIN_WIDTH = 450
const hasNumericValue = val => !(isNull(val) || parseInt(val) == 0)

function Badge({total}) {
  if (!hasNumericValue(total))
    return

  return <div className="badge">{displayMax(total)}</div>
}

function Icon({onClick: handleClick}) {
  const totalNew = useSelector(getUnreadCount)
  const iconClasses = useClasses([ 'fa-light', 'fa-messages', hasNumericValue(totalNew) ? 'active' : null ])

  return <div className="messaging-icon" onClick={handleClick}>
    <i className={iconClasses}></i>
    <Badge total={totalNew} />
  </div>
}

function Icons({selector, onClick: handleClick}) {
  const [ targets, setTargets ] = useState([])
  
  useEffect(() => {
   // Find all nodes with the class
    const nodes = Array.from(document.querySelectorAll(selector));
    setTargets(nodes);
  }, [])

  return <>
    { targets.map(el => createPortal((<Icon onClick={handleClick} /> ), el))}
  </>
}

function Portal({children}) {
  return createPortal(children, document.getElementById('messaging-header-icon-modal'))
}

function updateRect(current, setRect, { minWidth = MIN_WIDTH } = {}) {
  if (!current)
    return

  let left = distanceFromLeft(current)
  const top = distanceFromTop(current)

  const container = document.querySelector('#main-navbar > .container')
  const relativeLeft = left - distanceFromLeft(container)
  const maxWidth = container.offsetWidth
  
  let width = container.clientWidth - relativeLeft


  if (width < minWidth) {
    left -= (minWidth - width)
    width = minWidth
  }

  if (maxWidth && width > maxWidth) {
    left = 0
    width = maxWidth
  }

  setRect({left, top, width})
}

export function useMaintainMessages(messageIds) {
  const { messages } = useMessages(messageIds)
  useMessageThreads(messages.map(m => m.comment_thread_id))
  useOwners(messages.map(m => m.owner_id))
  useOwnerUsers(messages.map(m => [m.owner_id, m.posted_by]))
}

function App() {
  const [isOpen, setIsOpen] = useState(null)
  const [relativeIcon, setRelativeIcon] = useState(null)
  const [rect, setRect] = useState({})
  const [width, setWidth] = useState(null)

  const drawerRef = useWatchableRef()

  // Always have the most recent messages available
  const { messages, setPage, setAutoMerge } = useUnreadContext()
  useMaintainMessages(messages)
  
  useEffect(() => {
    if (isOpen) return

    setPage(0)
  }, [isOpen])

  useEffect(() => {
    if (!drawerRef.current || !isOpen) return

    const close = (evt) => {
      if (!document.body.contains(evt.target)) return
      if (drawerRef.current.contains(evt.target)) return

      setIsOpen(false)
    }

    // This is being set waaay too fast.
    setTimeout(() => window.addEventListener('click', close), 500)
    return () => window.removeEventListener('click', close)
  }, [drawerRef.version, isOpen])

  // Set Resizings
  useEffect(() => {
    if (!relativeIcon) return

    const onResize = () => updateRect(relativeIcon, setRect)

    setTimeout(onResize, 2500)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [relativeIcon])

  // Set Icons
  const handleIconClick = useCallback((evt) => {
    setRelativeIcon(evt.target)
    updateRect(evt.target, setRect)
    setIsOpen(true)
  }, [setRelativeIcon, setIsOpen])

  const isDrawerOpen = useMemo(() => isOpen && rect, [isOpen, rect])

  useEffect(() => {
    setAutoMerge(!isDrawerOpen)
  }, [isDrawerOpen])

  return <>
    <Icons selector=".messaging-inbox-header-icon" onClick={handleIconClick} />
    { isDrawerOpen ? <Portal><Drawer ref={drawerRef.ref} {...rect} onClose={() => setIsOpen(false)} /></Portal> : null }
  </>
}

export default App